<!-- Unified Onboarding Card -->
<div *ngIf="showCard()" class="unified-onboarding-card">

  <!-- Card Header: always visible -->
  <div class="d-flex justify-content-between align-items-start">
    <div class="d-flex align-items-center flex-grow-1 min-w-0">
      <div class="icon-wrapper me-2 me-sm-3">
        <i class="bi bi-rocket-takeoff fs-5"></i>
      </div>
      <div class="min-w-0">
        <h5 class="mb-0 onboarding-title">Getting Started</h5>
        <p class="onboarding-subtitle mb-0">Complete these steps to unlock flight logging, scheduling, and compliance reporting.</p>
      </div>
    </div>

    <!-- Dismiss Button (X) -->
    <button
      type="button"
      class="dismiss-btn ms-2"
      aria-label="Dismiss onboarding"
      (click)="dismiss()">
      <i class="bi bi-x-lg"></i>
    </button>
  </div>

  <!-- Progress Bar -->
  <div class="progress-section">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <span class="progress-label">{{ completedSummary() }}</span>
      <span class="progress-value">{{ onboardingService.combinedPercentage() }}%</span>
    </div>
    <div class="progress-track">
      <div
        class="progress-fill"
        role="progressbar"
        [style.width.%]="onboardingService.combinedPercentage()"
        [attr.aria-valuenow]="onboardingService.combinedPercentage()"
        aria-valuemin="0"
        aria-valuemax="100">
      </div>
    </div>
  </div>

  <!-- Compact CTA Row -->
  <div class="compact-actions mt-2">
    <button
      *ngIf="showContinueButton()"
      type="button"
      class="btn btn-sm btn-primary action-btn me-2"
      (click)="continueSetup()">
      <i class="bi bi-arrow-right-circle me-1"></i>
      Continue Setup
    </button>
    <button
      type="button"
      class="btn btn-sm btn-link toggle-btn p-0"
      (click)="toggleExpand()">
      {{ isExpanded() ? 'Hide details' : 'View checklist' }}
      <i class="bi ms-1" [ngClass]="isExpanded() ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
    </button>
  </div>

  <!-- Next Step Hint (compact view only) -->
  <div *ngIf="!isExpanded() && nextIncompleteStep()" class="next-step-hint mt-1">
    Next: <span class="next-step-title">{{ nextIncompleteStep() }}</span>
  </div>

  <!-- Expanded Checklist Detail -->
  <div *ngIf="isExpanded()" class="checklist-detail mt-3">

    <!-- Personal Setup Section -->
    <div class="section-header">
      <span class="section-label">Personal Setup</span>
      <span *ngIf="onboardingService.personalIncompleteCount() > 0" class="section-badge">
        {{ onboardingService.personalIncompleteCount() }} remaining
      </span>
    </div>

    <div class="checklist-items">
      <div
        *ngFor="let item of onboardingService.personalItems()"
        class="onboarding-item d-flex align-items-center"
        [class.clickable]="!item.isCompleted"
        (click)="openWizardAtStep(item)"
        [attr.role]="!item.isCompleted ? 'button' : null"
        [attr.tabindex]="!item.isCompleted ? 0 : -1">

        <!-- Completion Icon -->
        <div class="item-icon flex-shrink-0">
          <i class="bi" [ngClass]="item.isCompleted ? 'bi-check-circle-fill completed-icon' : 'bi-circle pending-icon'"></i>
        </div>

        <!-- Item Title + Optional Badge + Description -->
        <div class="item-content flex-grow-1 min-w-0">
          <div class="d-flex align-items-center">
            <span class="item-title" [class.item-completed]="item.isCompleted">
              {{ item.title }}
            </span>
            <span *ngIf="item.isOptional" class="optional-badge ms-2">Optional</span>
          </div>
          <div *ngIf="item.description" class="item-description">{{ item.description }}</div>
        </div>

        <!-- Arrow for incomplete clickable items -->
        <div class="item-arrow flex-shrink-0" *ngIf="!item.isCompleted">
          <i class="bi bi-chevron-right"></i>
        </div>
      </div>
    </div>

    <!-- Organization Setup Section (Owner/CS only) -->
    <div *ngIf="isOwnerOrCS()">
      <div class="section-divider"></div>

      <div class="section-header">
        <span class="section-label">Organization Setup</span>
        <span *ngIf="onboardingService.orgIncompleteCount() > 0" class="section-badge">
          {{ onboardingService.orgIncompleteCount() }} remaining
        </span>
      </div>

      <div class="checklist-items">
        <div
          *ngFor="let item of onboardingService.orgItems()"
          class="onboarding-item d-flex align-items-center"
          [class.clickable]="!item.isCompleted && item.actionRoute"
          (click)="navigateToItem(item)"
          [attr.role]="!item.isCompleted && item.actionRoute ? 'button' : null"
          [attr.tabindex]="!item.isCompleted && item.actionRoute ? 0 : -1">

          <!-- Completion Icon -->
          <div class="item-icon flex-shrink-0">
            <i class="bi" [ngClass]="item.isCompleted ? 'bi-check-circle-fill completed-icon' : 'bi-circle pending-icon'"></i>
          </div>

          <!-- Item Title + Subtitle + Optional Badge -->
          <div class="item-content flex-grow-1 min-w-0">
            <div class="d-flex align-items-center">
              <span class="item-title" [class.item-completed]="item.isCompleted">
                {{ item.title }}
              </span>
              <span *ngIf="item.isOptional" class="optional-badge ms-2">Optional</span>
            </div>
            <div *ngIf="item.subtitle" class="item-description">{{ item.subtitle }}</div>
          </div>

          <!-- Navigation Arrow (for incomplete clickable items) -->
          <div class="item-arrow flex-shrink-0" *ngIf="!item.isCompleted && item.actionRoute">
            <i class="bi bi-chevron-right"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Completion Celebration Card -->
<div *ngIf="showCelebration()" class="unified-onboarding-card celebration-card">
  <div class="text-center py-3 px-2">
    <i class="bi bi-check-circle-fill celebration-icon mb-2 d-block"></i>
    <h5 class="onboarding-title mb-1">Congratulations!</h5>
    <p class="onboarding-subtitle mb-0">Your account is fully set up and ready to go.</p>
  </div>
</div>

<!-- Onboarding Wizard Modal (embedded as child) -->
<app-onboarding-wizard-modal
  #onboardingWizardModal
  (completed)="onWizardCompleted()"
  (cancelled)="onWizardCancelled()">
</app-onboarding-wizard-modal>
